import { Box, CircularProgress, debounce, Dialog, dialogClasses, DialogContent, InputAdornment, Stack, Typography } from '@mui/material'
import { C1BorderRadius } from 'c1ui/theme/C1BorderRadius'
import { C1Icon } from 'components/core/icons/C1Icon'
import { TextField } from 'components/core/textfield/index'
import { ArrowDown, ArrowUp, CornerDownLeft, Search } from 'lucide-react'
import { AccessReviewSearchServiceSearchRequest } from 'pbts/c1/api/accessreview/v1/accessreview_search'
import { SearchAppsExpandedRequest } from 'pbts/c1/api/app/v1/app'
import { AppEntitlementSearchServiceSearchRequest as SearchEntitlementsRequest } from 'pbts/c1/api/app/v1/app_entitlement'
import { SearchPoliciesRequest } from 'pbts/c1/api/policy/v1/policy'
import { SearchUsersRequest } from 'pbts/c1/api/user/v1/user'
import { ChangeEvent, FC, KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { searchAccessReviewApi } from 'redux_toolkit/access-reviews'
import { appSearchApi } from 'redux_toolkit/apps'
import { appEntitlementsSearchApi } from 'redux_toolkit/entitlements'
import { searchPoliciesApi } from 'redux_toolkit/policies'
import { userSearchApi } from 'redux_toolkit/users'
import { ListSearchResults } from './ListSearchResults'

type SpotlightProps = {
    open: boolean
    onClose: () => void
}

export const Spotlight: FC<SpotlightProps> = ({ open, onClose }) => {
    const [isEmptyState, setIsEmptyState] = useState(true)
    const [searchQuery, setSearchQuery] = useState('')
    const [loadingError, setLoadingError] = useState('')
    const [selectedIndex, setSelectedIndex] = useState(0)
    const [isWaitingForDebounce, setIsWaitingForDebounce] = useState(false)
    const totalResultsRef = useRef(0)
    const activateSelectedRef = useRef<(() => void) | null>(null)
    const pageSize = 10
    const intl = useIntl()

    const {
        trigger: triggerSearchUsers,
        data: users,
        isLoading: isSearchingForUsers,
        isError: isSearchUsersError,
    } = userSearchApi.useLazySearch()

    const {
        trigger: triggerSearchEntitlements,
        data: entitlements,
        isLoading: isSearchingForEntitlements,
        isError: isSearchEntitlementsError,
    } = appEntitlementsSearchApi.useLazySearch()

    const {
        trigger: triggerSearchApps,
        data: apps,
        isLoading: isSearchingForApps,
        isError: isSearchAppsError,
    } = appSearchApi.useLazySearchAppsExpanded()

    const {
        trigger: triggerSearchAccessReviews,
        data: accessReviews,
        isLoading: isSearchingForAccessReviews,
        isError: isSearchAccessReviewsError,
    } = searchAccessReviewApi.useLazySearch()

    const {
        trigger: triggerSearchPolicies,
        data: policies,
        isLoading: isSearchingForPolicies,
        isError: isSearchPoliciesError,
    } = searchPoliciesApi.useLazySearch()

    const handleClose = (): void => {
        setIsEmptyState(true)
        setSearchQuery('')
        setSelectedIndex(0)
        onClose()
    }

    const handleKeyDown = useCallback((event: KeyboardEvent): void => {
        if (event.key === 'ArrowDown') {
            event.preventDefault()
            setSelectedIndex((prev) => Math.min(prev + 1, totalResultsRef.current - 1))
        } else if (event.key === 'ArrowUp') {
            event.preventDefault()
            setSelectedIndex((prev) => Math.max(prev - 1, 0))
        } else if (event.key === 'Enter') {
            event.preventDefault()
            if (activateSelectedRef.current) {
                activateSelectedRef.current()
            }
        }
    }, [])

    // Handle immediate state updates (for instant client-side filtering)
    const onInputChange = useCallback((event: ChangeEvent<HTMLInputElement>): void => {
        const searchText = event.target.value
        setSearchQuery(searchText)
        setSelectedIndex(0) // Reset selection when query changes
        setIsEmptyState(searchText === '')
    }, [])

    // Handle debounced API searches
    const onFilterChange = useCallback(
        (searchText: string) => {
            if (searchText === '') {
                return
            }
            triggerSearchUsers(
                SearchUsersRequest.create({
                    query: searchText,
                    expandMask: { paths: ['*'] },
                }),
                {
                    pageSize,
                    notify: true,
                    actionable: !!searchText,
                },
            )
            triggerSearchEntitlements(
                SearchEntitlementsRequest.create({
                    query: searchText,
                    expandMask: { paths: ['*'] },
                }),
                {
                    pageSize,
                    notify: true,
                    actionable: !!searchText,
                },
            )
            triggerSearchAccessReviews(
                AccessReviewSearchServiceSearchRequest.create({
                    query: searchText,
                    expandMask: { paths: ['*'] },
                }),
                {
                    pageSize,
                    notify: true,
                    actionable: !!searchText,
                },
            )
            triggerSearchApps(
                SearchAppsExpandedRequest.create({
                    query: searchText,
                    expandMask: { paths: ['*'] },
                }),
                {
                    pageSize,
                    notify: true,
                    actionable: !!searchText,
                },
            )
            triggerSearchPolicies(
                SearchPoliciesRequest.create({
                    query: searchText,
                }),
                {
                    pageSize,
                    notify: true,
                    actionable: !!searchText,
                },
            )
        },
        [triggerSearchUsers, triggerSearchEntitlements, triggerSearchAccessReviews, triggerSearchApps, triggerSearchPolicies, pageSize],
    )

    const triggerDebouncedSearch = useMemo(() => debounce((searchText: string) => {
        setIsWaitingForDebounce(false)
        onFilterChange(searchText)
    }, 400), [onFilterChange])

    // Combined handler: instant state update + debounced API search
    const handleInputChange = useCallback((event: ChangeEvent<HTMLInputElement>): void => {
        const value = event.target.value
        onInputChange(event)
        setIsWaitingForDebounce(value.length > 0)
        triggerDebouncedSearch(value)
    }, [onInputChange, triggerDebouncedSearch])

    useEffect(() => {
        const isStillSearching = isSearchingForUsers || isSearchingForEntitlements ||
            isSearchingForAccessReviews || isSearchingForApps || isSearchingForPolicies
        if (isStillSearching) {
            return
        }
        const erroredCalls: string[] = []
        if (isSearchUsersError) {
            erroredCalls.push(intl.formatMessage({ id: 'spotlight_users', defaultMessage: 'Users' }))
        }
        if (isSearchEntitlementsError) {
            erroredCalls.push(intl.formatMessage({ id: 'spotlight_entitlements', defaultMessage: 'Entitlements' }))
        }
        if (isSearchAccessReviewsError) {
            erroredCalls.push(intl.formatMessage({ id: 'spotlight_access_reviews', defaultMessage: 'Access reviews' }))
        }
        if (isSearchAppsError) {
            erroredCalls.push(intl.formatMessage({ id: 'spotlight_apps', defaultMessage: 'Applications' }))
        }
        if (isSearchPoliciesError) {
            erroredCalls.push(intl.formatMessage({ id: 'spotlight_policies', defaultMessage: 'Policies' }))
        }
        const errorCount = erroredCalls.length
        if (errorCount === 5) {
            setLoadingError(intl.formatMessage({ id: 'search_error_all', defaultMessage: 'Failed to fetch results. Please try again later.' }))
        } else if (errorCount) {
            const concatenatedObjects = erroredCalls.join(', ').replace(/,([^,]*)$/, ' and$1')
            setLoadingError(intl.formatMessage({ id: 'search_error', defaultMessage: 'Failed to fetch results for {objects}' }, {
                objects: concatenatedObjects,
            }))
        } else {
            // No error, clear the error message
            setLoadingError('')
        }
    }, [intl, isSearchUsersError, isSearchEntitlementsError, isSearchAccessReviewsError,
        isSearchAppsError, isSearchPoliciesError, isSearchingForUsers, isSearchingForEntitlements,
        isSearchingForAccessReviews, isSearchingForApps, isSearchingForPolicies])

    const isLoading = isWaitingForDebounce || isSearchingForUsers || isSearchingForEntitlements ||
        isSearchingForAccessReviews || isSearchingForApps || isSearchingForPolicies

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            transitionDuration={0}
            sx={{
                [`& .${dialogClasses.container}`]: {
                    alignItems: 'flex-start',
                    paddingTop: '12vh',
                },
                [`& .${dialogClasses.paper}`]: {
                    maxWidth: '600px',
                    width: '100%',
                    overflow: 'hidden',
                    borderRadius: C1BorderRadius.medium,
                },
            }}
        >
            <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', maxHeight: '60vh' }}>
                <Box sx={{ flexShrink: 0 }}>
                    <TextField
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        placeholder={intl.formatMessage({
                            id: 'spotlight_search_placeholder',
                            defaultMessage: 'Search or navigate to',
                        })}
                        inputProps={{ 'aria-label': 'search' }}
                        InputProps={{
                            sx: {
                                border: 'none',

                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderTop: 'none',
                                    borderLeft: 'none',
                                    borderRight: 'none',
                                },

                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderWidth: 3,
                                },
                            },
                            startAdornment: (
                                <InputAdornment position="start">
                                    <C1Icon icon={Search} size="sm" sx={{ color: 'text.secondary' }} />
                                </InputAdornment>
                            ),
                            endAdornment: isLoading ? (
                                <InputAdornment position="end">
                                    <CircularProgress size={20} />
                                </InputAdornment>
                            ) : null,
                        }}
                        autoComplete="off"
                        autoFocus
                        fullWidth
                        sx={{
                            '& .MuiInputBase-root': {
                                fontSize: '1.125rem',
                                borderRadius: 0,
                            },
                            '& .MuiInputBase-input': {
                                py: 2,
                            },
                        }}
                    />
                    {!isEmptyState && (
                        <Stack spacing={2} py={1} direction="row" alignItems="center" borderBottom={1} borderColor="divider" sx={{ backgroundColor: 'grey.50', px: (t) => `calc(${t.spacing(2)} + ${t.spacing(2)})` }}>
                            <Stack spacing={1} direction="row" alignItems="center">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                                    <C1Icon icon={ArrowUp} size="sm" sx={{ color: 'text.secondary' }} />
                                    <C1Icon icon={ArrowDown} size="sm" sx={{ color: 'text.secondary' }} />
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                    <FormattedMessage id="spotlight_keyboard_nav" defaultMessage="Navigate" />
                                </Typography>
                            </Stack>
                            <Stack spacing={1} direction="row" alignItems="center">
                                <Box sx={{ display: 'flex', alignItems: 'center', px: 1, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                                    <C1Icon icon={CornerDownLeft} size="sm" sx={{ color: 'text.secondary' }} />
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                    <FormattedMessage id="spotlight_keyboard_select" defaultMessage="Select" />
                                </Typography>
                            </Stack>
                            <Stack spacing={1} direction="row" alignItems="center">
                                <Typography variant="monospace" sx={{ fontSize: 11, px: 1, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                                    ESC
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    <FormattedMessage id="spotlight_keyboard_dismiss" defaultMessage="Close" />
                                </Typography>
                            </Stack>
                        </Stack>
                    )}
                </Box>
                <Box sx={{ px: 2, pb: 2, overflowY: 'auto', flexGrow: 1 }}>
                    <ListSearchResults
                        entitlements={entitlements[0]}
                        users={users[0]}
                        campaigns={accessReviews[0]}
                        apps={apps[0]}
                        policies={policies[0]}
                        isLoading={isLoading}
                        isEmpty={isEmptyState}
                        handleClose={handleClose}
                        loadingErrorText={loadingError}
                        searchQuery={searchQuery}
                        selectedIndex={selectedIndex}
                        totalResultsRef={totalResultsRef}
                        activateSelectedRef={activateSelectedRef}
                    />
                </Box>
            </DialogContent>
        </Dialog>
    )
}
